import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STARTUP_TARGET = 15000;
const STARTUP_PAGE_LIMIT = 200;
const STARTUP_SAMPLE_CAP = 2000;
const SIGNAL_LIMIT = 100;
const AUTONOMOUS_DISCOVERY_PRESET = {
  background: true,
  fund_limit: 400,
  max_portfolio_pages_per_fund: 4,
  max_startups_per_fund: 120,
  fund_concurrency: 12,
  portfolio_page_concurrency: 24,
  adapter_max_retries: 2,
  adapter_backoff_base_seconds: 0.5,
  adapter_backoff_cap_seconds: 8,
  run_label: "ops_autonomous_fund_sweep",
};

const AUTONOMOUS_ENRICHMENT_PRESET = {
  background: true,
  limit: 50,
  force: false,
  execute: false,
};

const opsClient = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const safeArray = (value) => (Array.isArray(value) ? value : []);

const hasText = (value) => typeof value === "string" && value.trim().length > 0;

const toValidDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toPercent = (count, total) => {
  if (!total) {
    return 0;
  }

  return Math.round(((count / total) * 100 + Number.EPSILON) * 10) / 10;
};

const formatLabel = (value) =>
  `${value || "other"}`
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getProviderRank = (status) => {
  switch (status) {
    case "available":
      return 0;
    case "no_api_key":
      return 1;
    case "rate_limited":
      return 2;
    case "unavailable":
      return 3;
    default:
      return 4;
  }
};

const getBackfillStatus = (lastRun) => {
  if (!lastRun) {
    return "empty";
  }

  if (lastRun.error) {
    return "failed";
  }

  if (Number(lastRun.errors || 0) > 0) {
    return "attention";
  }

  return "healthy";
};

const sourceFamilyDefinitions = [
  {
    key: "vc_portfolio",
    label: "VC portfolio",
    description: "Startup has investor portfolio lineage on the canonical record.",
    matches: (startup) => hasText(startup.source_vc) || safeArray(startup.source_vcs).length > 0,
  },
  {
    key: "company_website",
    label: "Company website",
    description: "Public company website is attached to the startup record.",
    matches: (startup) => hasText(startup.website),
  },
  {
    key: "team_page",
    label: "Team page",
    description: "Operator can drill into founder or team sourcing surfaces.",
    matches: (startup) => hasText(startup.team_page_url),
  },
  {
    key: "careers_page",
    label: "Careers / jobs",
    description: "Hiring surface is present for crawl and freshness coverage.",
    matches: (startup) => hasText(startup.careers_url),
  },
  {
    key: "public_deck",
    label: "Public deck",
    description: "Deck URL or latest deck metadata is available.",
    matches: (startup) => Boolean(startup.latest_deck) || safeArray(startup.deck_urls).length > 0,
  },
  {
    key: "signal_trail",
    label: "Signal trail",
    description: "Startup already carries at least one surfaced signal.",
    matches: (startup) => Boolean(startup.last_signal),
  },
];

const normalizationFieldDefinitions = [
  {
    key: "name",
    label: "Company name",
    description: "Canonical company name is populated.",
    matches: (startup) => hasText(startup.name),
  },
  {
    key: "description",
    label: "One-line description",
    description: "Operator-facing English summary is present.",
    matches: (startup) => hasText(startup.description),
  },
  {
    key: "sector",
    label: "Sector",
    description: "Vertical taxonomy is normalized onto the startup record.",
    matches: (startup) => hasText(startup.sector),
  },
  {
    key: "stage",
    label: "Stage",
    description: "Funding stage is present and not left as unknown.",
    matches: (startup) => hasText(startup.stage) && startup.stage.trim().toLowerCase() !== "unknown",
  },
  {
    key: "hq_city",
    label: "HQ city",
    description: "Location string is operator-ready for English UI surfaces.",
    matches: (startup) => hasText(startup.city),
  },
  {
    key: "website",
    label: "Website",
    description: "Canonical website is available for recrawl and review.",
    matches: (startup) => hasText(startup.website),
  },
];

async function fetchStartupSample(firstPageData, totalStartups) {
  const firstPageStartups = safeArray(firstPageData?.startups);
  const knownTotal = Number(firstPageData?.total ?? totalStartups ?? 0);
  const sampleTarget = Math.min(Math.max(knownTotal, firstPageStartups.length), STARTUP_SAMPLE_CAP);

  if (!firstPageStartups.length || sampleTarget <= STARTUP_PAGE_LIMIT) {
    return {
      startups: firstPageStartups.slice(0, sampleTarget || firstPageStartups.length),
      sampledCount: Math.min(firstPageStartups.length, sampleTarget || firstPageStartups.length),
      isSampled: knownTotal > firstPageStartups.length,
    };
  }

  const pageCount = Math.ceil(sampleTarget / STARTUP_PAGE_LIMIT);
  const pageRequests = [];

  for (let pageIndex = 1; pageIndex < pageCount; pageIndex += 1) {
    pageRequests.push(
      opsClient.get("/startups/", {
        params: {
          sort_by: "recent",
          limit: STARTUP_PAGE_LIMIT,
          skip: pageIndex * STARTUP_PAGE_LIMIT,
        },
      })
    );
  }

  const pageResults = await Promise.allSettled(pageRequests);
  const extraStartups = pageResults.flatMap((result) =>
    result.status === "fulfilled" ? safeArray(result.value?.data?.startups) : []
  );
  const startups = [...firstPageStartups, ...extraStartups].slice(0, sampleTarget);

  return {
    startups,
    sampledCount: startups.length,
    isSampled: knownTotal > startups.length,
  };
}

function buildSourceCoverage(startups, totalStartups) {
  const sampledCount = startups.length;
  const families = sourceFamilyDefinitions
    .map((family) => {
      const coveredCount = startups.filter(family.matches).length;

      return {
        ...family,
        coveredCount,
        coveragePercent: toPercent(coveredCount, sampledCount),
      };
    })
    .sort((left, right) => right.coveredCount - left.coveredCount);

  return {
    sampledCount,
    totalStartups,
    isSampled: totalStartups > sampledCount,
    families,
  };
}

function buildNormalizationProgress(startups, totalStartups) {
  const sampledCount = startups.length;
  const fields = normalizationFieldDefinitions.map((field) => {
    const populatedCount = startups.filter(field.matches).length;

    return {
      ...field,
      populatedCount,
      coveragePercent: toPercent(populatedCount, sampledCount),
    };
  });

  const totalFieldSlots = sampledCount * fields.length;
  const populatedFieldSlots = fields.reduce((sum, field) => sum + field.populatedCount, 0);

  return {
    sampledCount,
    totalStartups,
    isSampled: totalStartups > sampledCount,
    overallCoveragePercent: toPercent(populatedFieldSlots, totalFieldSlots),
    fields,
  };
}

function buildSignalMetrics(recentSignalRows, startups) {
  const signals = safeArray(recentSignalRows)
    .map((row) => {
      if (!row?.signal) {
        return null;
      }

      return {
        ...row.signal,
        startupId: row.startup_id,
        startupName: row.startup_name,
      };
    })
    .filter(Boolean);

  const signalTypeCounts = signals.reduce((counts, signal) => {
    const key = hasText(signal.signal_type) ? signal.signal_type : "other";
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});

  const byType = Object.entries(signalTypeCounts)
    .map(([key, count]) => ({
      key,
      label: formatLabel(key),
      count,
      coveragePercent: toPercent(count, signals.length),
    }))
    .sort((left, right) => right.count - left.count);

  const now = Date.now();
  const freshnessDefinitions = [
    {
      key: "last_24h",
      label: "Last 24h",
      matches: (timestamp) => now - timestamp <= 24 * 60 * 60 * 1000,
    },
    {
      key: "last_7d",
      label: "Last 7d",
      matches: (timestamp) => now - timestamp <= 7 * 24 * 60 * 60 * 1000,
    },
    {
      key: "last_30d",
      label: "Last 30d",
      matches: (timestamp) => now - timestamp <= 30 * 24 * 60 * 60 * 1000,
    },
    {
      key: "older",
      label: "Older",
      matches: (timestamp) => now - timestamp > 30 * 24 * 60 * 60 * 1000,
    },
  ];

  const freshness = freshnessDefinitions.map((bucket) => {
    const count = signals.filter((signal) => {
      const date = toValidDate(signal.timestamp);
      return date ? bucket.matches(date.getTime()) : false;
    }).length;

    return {
      key: bucket.key,
      label: bucket.label,
      count,
      coveragePercent: toPercent(count, signals.length),
    };
  });

  const latestSignalAt = signals.reduce((latest, signal) => {
    const date = toValidDate(signal.timestamp);

    if (!date) {
      return latest;
    }

    if (!latest || date > latest) {
      return date;
    }

    return latest;
  }, null);

  const startupsWithSignals = startups.filter((startup) => Boolean(startup.last_signal)).length;

  return {
    sampledStartupCount: startups.length,
    startupsWithSignals,
    startupSignalCoveragePercent: toPercent(startupsWithSignals, startups.length),
    recentSignalCount: signals.length,
    latestSignalAt: latestSignalAt ? latestSignalAt.toISOString() : null,
    byType,
    freshness,
  };
}

function buildJobHealth(stats, health, providerPayload) {
  const providers = Object.entries(providerPayload?.providers || {})
    .map(([key, provider]) => ({
      key,
      name: provider?.provider || key,
      status: provider?.status || "unknown",
      requestCount: Number(provider?.request_count || 0),
      errorCount: Number(provider?.error_count || 0),
      error: provider?.error || null,
    }))
    .sort((left, right) => getProviderRank(left.status) - getProviderRank(right.status) || left.name.localeCompare(right.name));

  const availableProviderCount = providers.filter((provider) => provider.status === "available").length;
  const lastRun = stats?.last_sourcing_run || null;

  return {
    database: {
      status: (health?.database?.status ?? health?.database) === "connected" ? "healthy" : (health?.database?.status ?? health?.database) ? "attention" : "unknown",
      label: health?.database?.status ?? health?.database ?? "unknown",
      checkedAt: health?.timestamp || null,
    },
    providers,
    availableProviderCount,
    totalProviderCount: providers.length,
    backfill: {
      status: getBackfillStatus(lastRun),
      scrapedCount: Number(lastRun?.scraped || 0),
      newCount: Number(lastRun?.new || 0),
      updatedCount: Number(lastRun?.updated || 0),
      errorCount: Number(lastRun?.errors || 0),
      startedAt: lastRun?.started_at || null,
      completedAt: lastRun?.completed_at || null,
    },
    queueMetricsAvailable: false,
  };
}

function buildWarnings({ statsAvailable, firstPageAvailable, signalsAvailable, providersAvailable, healthAvailable, isSampled }) {
  const warnings = [];

  if (!statsAvailable) {
    warnings.push("Sourcing rollups are unavailable, so progress and backfill summaries may be partial.");
  }

  if (!firstPageAvailable) {
    warnings.push("Startup sample data did not load, so coverage and normalization panels may be empty.");
  } else if (isSampled) {
    warnings.push(`Coverage and normalization are based on the latest ${STARTUP_SAMPLE_CAP.toLocaleString()} startup records.`);
  }

  if (!signalsAvailable) {
    warnings.push("Recent signal telemetry is unavailable right now.");
  }

  if (!providersAvailable || !healthAvailable) {
    warnings.push("Infrastructure health is partially loaded.");
  }

  return warnings;
}

export const opsApi = {
  async getWeek2Snapshot() {
    const [statsResult, healthResult, providersResult, signalsResult, firstPageResult] = await Promise.allSettled([
      opsClient.get("/sourcing/stats"),
      opsClient.get("/health"),
      opsClient.get("/providers/status"),
      opsClient.get("/startups/signals", { params: { limit: SIGNAL_LIMIT } }),
      opsClient.get("/startups/", {
        params: {
          sort_by: "recent",
          limit: STARTUP_PAGE_LIMIT,
          skip: 0,
        },
      }),
    ]);

    const stats = statsResult.status === "fulfilled" ? statsResult.value.data : null;
    const health = healthResult.status === "fulfilled" ? healthResult.value.data : null;
    const providers = providersResult.status === "fulfilled" ? providersResult.value.data : null;
    const signals = signalsResult.status === "fulfilled" ? safeArray(signalsResult.value.data?.signals) : [];
    const firstPageData = firstPageResult.status === "fulfilled" ? firstPageResult.value.data : null;

    const totalStartups = Number(stats?.total_startups ?? firstPageData?.total ?? 0);
    const autoSourced = Number(stats?.auto_sourced || 0);
    const userAdded = Number(stats?.user_added || Math.max(totalStartups - autoSourced, 0));
    const startupSample = await fetchStartupSample(firstPageData, totalStartups);
    const sourceCoverage = buildSourceCoverage(startupSample.startups, totalStartups);
    const normalization = buildNormalizationProgress(startupSample.startups, totalStartups);
    const jobHealth = buildJobHealth(stats, health, providers);
    const signalMetrics = buildSignalMetrics(signals, startupSample.startups);
    const warnings = buildWarnings({
      statsAvailable: statsResult.status === "fulfilled",
      firstPageAvailable: firstPageResult.status === "fulfilled",
      signalsAvailable: signalsResult.status === "fulfilled",
      providersAvailable: providersResult.status === "fulfilled",
      healthAvailable: healthResult.status === "fulfilled",
      isSampled: startupSample.isSampled,
    });

    return {
      loadedAt: new Date().toISOString(),
      targetStartups: STARTUP_TARGET,
      summary: {
        totalStartups,
        autoSourced,
        userAdded,
        remainingStartups: Math.max(STARTUP_TARGET - totalStartups, 0),
        progressPercent: toPercent(totalStartups, STARTUP_TARGET),
        sampledCount: startupSample.sampledCount,
        sampleIsPartial: startupSample.isSampled,
        recentSignalCount: signalMetrics.recentSignalCount,
        availableProviderCount: jobHealth.availableProviderCount,
        totalProviderCount: jobHealth.totalProviderCount,
        lastBackfillAt: jobHealth.backfill.completedAt || jobHealth.backfill.startedAt || null,
      },
      sourceCoverage,
      normalization,
      jobHealth,
      signalMetrics,
      meta: {
        hasAnyData:
          totalStartups > 0 ||
          startupSample.sampledCount > 0 ||
          signalMetrics.recentSignalCount > 0 ||
          jobHealth.totalProviderCount > 0,
        warnings,
      },
    };
  },

  async startAutonomousDiscovery(overrides = {}) {
    const response = await opsClient.post("/sourcing/discovery/run", {
      ...AUTONOMOUS_DISCOVERY_PRESET,
      ...overrides,
    });
    return response.data;
  },

  async queueAutonomousEnrichment(overrides = {}) {
    const response = await opsClient.post("/sourcing/run", {
      ...AUTONOMOUS_ENRICHMENT_PRESET,
      ...overrides,
    });
    return response.data;
  },
};

export default opsApi;
